import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
} from '@pflegenavi/shared-frontend/platform';
import type {
  TransactionType,
  TransactionSourceType,
  TransactionInitiatedBy,
  SourceOfMandate,
} from '@pflegenavi/shared/api';

export interface GetTransactionsPhoenixParams {
  page?: number;
  page_size?: number;
  nursing_home_id?: string;
  resident_id?: string;
  family_member_id?: string | null;
  type?: TransactionType[];
  source_type?: TransactionSourceType[];
  initiated_by?: TransactionInitiatedBy[];
  creation_date_range?: [Date | undefined, Date | undefined];
  submission_date_range?: [Date | undefined, Date | undefined];
  receipt_date_range?: [Date | undefined, Date | undefined];
  service_provider_ids?: string[];
  receipt_type_ids?: string[];
  creator_ids?: string[];
  search?: string;
  order_by?: string[];
  order_directions?: Array<'asc' | 'desc'>;
}

export interface ReceiptTypePhoenix {
  id: string;
  name: string;
}

export interface ServiceProviderPhoenix {
  id: string;
  name: string;
}

export interface TransactionPhoenixReceipt {
  id: string;
  receipt_date: string;
  receipt_not_available: boolean;
  created_on: string;
}

export interface TransactionPhoenixRecurringItem {
  id: string;
  name: string;
}

export interface TransactionPhoenixResident {
  id: string;
  firstname: string;
  lastname: string;
}

export interface TransactionPhoenixDto {
  id: string;
  type: TransactionType;
  source_type: TransactionSourceType;
  initiated_by: TransactionInitiatedBy;
  amount: number;
  additional_fee_amount: number;
  included_fee_amount: number;
  additional_fees_diverted_to_pflegenavi: boolean;
  creation_date: string; // The creation date field
  submission_date: string | null;
  receipt_date: string | null;
  created_on: string; // This is inserted_at (ISO date string)
  creator_id: string;
  creator_name: string;
  notes: string | null;
  cancelled: boolean;
  disputed: boolean;
  cancelled_submission_date: string | null;
  sequence_id: string;
  resident: TransactionPhoenixResident;
  family_member_id: string | null;
  receipt: TransactionPhoenixReceipt | null;
  receipt_image_ids: string[];
  recurring_item: TransactionPhoenixRecurringItem | null;
  receipt_type: ReceiptTypePhoenix | null;
  service_provider: ServiceProviderPhoenix | null;
  creator?: {
    first_name: string;
    last_name: string;
  };
  status?: string;
  receipt_state?: string;
  source_of_mandate?: SourceOfMandate | null;
}

export interface GetTransactionsPhoenixResult {
  data: TransactionPhoenixDto[];
  meta: {
    current_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
    next_page: number | null;
    previous_page: number | null;
  };
}

export interface ITransactionPhoenixApi extends Api {
  getTransactions(dataIn: {
    params: GetTransactionsPhoenixParams;
  }): Promise<GetTransactionsPhoenixResult>;
}

export class TransactionPhoenixApi implements ITransactionPhoenixApi {
  baseUrl: string;

  getTransactions: ITransactionPhoenixApi['getTransactions'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.getTransactions = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams();

        // Pagination params
        if (params.page !== undefined) {
          query.append('page', params.page.toString());
        }
        if (params.page_size !== undefined) {
          query.append('page_size', params.page_size.toString());
        }

        // Sorting params
        if (params.order_by) {
          params.order_by.forEach((field) => {
            query.append('order_by[]', field);
          });
        }
        if (params.order_directions) {
          params.order_directions.forEach((direction) => {
            query.append('order_directions[]', direction);
          });
        }

        // Build filters
        let filterIndex = 0;

        // Nursing home filter
        if (params.nursing_home_id) {
          query.append(`filters[${filterIndex}][field]`, 'nursing_home_id');
          query.append(`filters[${filterIndex}][op]`, '==');
          query.append(
            `filters[${filterIndex}][value]`,
            params.nursing_home_id
          );
          filterIndex++;
        }

        // Resident filter
        if (params.resident_id) {
          query.append(`filters[${filterIndex}][field]`, 'resident_id');
          query.append(`filters[${filterIndex}][op]`, '==');
          query.append(`filters[${filterIndex}][value]`, params.resident_id);
          filterIndex++;
        }

        // Family member filter
        if (params.family_member_id !== undefined) {
          query.append(`filters[${filterIndex}][field]`, 'family_member_id');
          query.append(
            `filters[${filterIndex}][op]`,
            params.family_member_id === null ? 'empty' : '=='
          );
          if (params.family_member_id !== null) {
            query.append(
              `filters[${filterIndex}][value]`,
              params.family_member_id
            );
          }
          filterIndex++;
        }

        // Type filter
        if (params.type && params.type.length > 0) {
          query.append(`filters[${filterIndex}][field]`, 'type');
          query.append(`filters[${filterIndex}][op]`, 'in');
          params.type.forEach((t) => {
            query.append(`filters[${filterIndex}][value][]`, t);
          });
          filterIndex++;
        }

        // Source type filter
        if (params.source_type && params.source_type.length > 0) {
          query.append(`filters[${filterIndex}][field]`, 'source_type');
          query.append(`filters[${filterIndex}][op]`, 'in');
          params.source_type.forEach((st) => {
            query.append(`filters[${filterIndex}][value][]`, st);
          });
          filterIndex++;
        }

        // Initiated by filter
        if (params.initiated_by && params.initiated_by.length > 0) {
          query.append(`filters[${filterIndex}][field]`, 'initiated_by');
          query.append(`filters[${filterIndex}][op]`, 'in');
          params.initiated_by.forEach((ib) => {
            query.append(`filters[${filterIndex}][value][]`, ib);
          });
          filterIndex++;
        }

        // Date range filters
        if (params.creation_date_range && params.creation_date_range[0]) {
          query.append(`filters[${filterIndex}][field]`, 'creation_date');
          query.append(`filters[${filterIndex}][op]`, '>=');
          query.append(
            `filters[${filterIndex}][value]`,
            params.creation_date_range[0].toISOString().split('T')[0]
          );
          filterIndex++;
        }
        if (params.creation_date_range && params.creation_date_range[1]) {
          query.append(`filters[${filterIndex}][field]`, 'creation_date');
          query.append(`filters[${filterIndex}][op]`, '<=');
          query.append(
            `filters[${filterIndex}][value]`,
            params.creation_date_range[1].toISOString().split('T')[0]
          );
          filterIndex++;
        }

        if (params.submission_date_range && params.submission_date_range[0]) {
          query.append(`filters[${filterIndex}][field]`, 'submission_date');
          query.append(`filters[${filterIndex}][op]`, '>=');
          query.append(
            `filters[${filterIndex}][value]`,
            params.submission_date_range[0].toISOString()
          );
          filterIndex++;
        }
        if (params.submission_date_range && params.submission_date_range[1]) {
          query.append(`filters[${filterIndex}][field]`, 'submission_date');
          query.append(`filters[${filterIndex}][op]`, '<=');
          query.append(
            `filters[${filterIndex}][value]`,
            params.submission_date_range[1].toISOString()
          );
          filterIndex++;
        }

        if (params.receipt_date_range && params.receipt_date_range[0]) {
          query.append(`filters[${filterIndex}][field]`, 'receipt_date');
          query.append(`filters[${filterIndex}][op]`, '>=');
          query.append(
            `filters[${filterIndex}][value]`,
            params.receipt_date_range[0].toISOString().split('T')[0]
          );
          filterIndex++;
        }
        if (params.receipt_date_range && params.receipt_date_range[1]) {
          query.append(`filters[${filterIndex}][field]`, 'receipt_date');
          query.append(`filters[${filterIndex}][op]`, '<=');
          query.append(
            `filters[${filterIndex}][value]`,
            params.receipt_date_range[1].toISOString().split('T')[0]
          );
          filterIndex++;
        }

        // Service provider filter
        if (
          params.service_provider_ids &&
          params.service_provider_ids.length > 0
        ) {
          query.append(`filters[${filterIndex}][field]`, 'service_provider_id');
          query.append(`filters[${filterIndex}][op]`, 'in');
          params.service_provider_ids.forEach((id) => {
            query.append(`filters[${filterIndex}][value][]`, id);
          });
          filterIndex++;
        }

        // Receipt type filter
        if (params.receipt_type_ids && params.receipt_type_ids.length > 0) {
          query.append(`filters[${filterIndex}][field]`, 'receipt_type_id');
          query.append(`filters[${filterIndex}][op]`, 'in');
          params.receipt_type_ids.forEach((id) => {
            query.append(`filters[${filterIndex}][value][]`, id);
          });
          filterIndex++;
        }

        // Creator filter
        if (params.creator_ids && params.creator_ids.length > 0) {
          query.append(`filters[${filterIndex}][field]`, 'creator_id');
          query.append(`filters[${filterIndex}][op]`, 'in');
          params.creator_ids.forEach((id) => {
            query.append(`filters[${filterIndex}][value][]`, id);
          });
          filterIndex++;
        }

        // Search param (compound: notes, service_provider_name, receipt_type_name, amount)
        if (params.search) {
          query.append('search', params.search);
        }

        return `${this.baseUrl}/transactions?${query.toString()}`;
      },
    });
  }
}
